import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { PageLayout } from "../components/PageLayout";

/**
 * Shared template for legal pages (Terms, Privacy, etc.)
 *
 * Props:
 *  - title: HTML document title
 *  - breadcrumb: [{ label, to? }]
 *  - tag: { icon, label }
 *  - headline: { prefix, accent, suffix }
 *  - subtitle: string
 *  - lastUpdated?: string
 *  - sections: [{ id, icon, title, body, bullets?, tag? }]
 *  - ctaTitle?: string
 *  - ctaDescription?: string
 *  - ctaLinks?: [{ label, to?, href?, primary?, testId? }]
 *  - testIdPrefix: string
 */
export const LegalPage = ({
  title,
  breadcrumb = [],
  tag,
  headline,
  subtitle,
  lastUpdated,
  sections = [],
  ctaTitle,
  ctaDescription,
  ctaLinks = [],
  testIdPrefix = "legal",
}) => {
  const TagIcon = tag?.icon;

  return (
    <PageLayout title={title} showBackgroundOrbs>
      <section className="relative osx-noise">
        <div className="absolute inset-0 osx-grid-bg opacity-50" aria-hidden />
        <div className="osx-container relative pt-14 sm:pt-20 pb-10">
          {breadcrumb.length > 0 && (
            <nav className="flex items-center gap-2 text-xs mb-4" style={{ color: "var(--osx-text-muted)" }}>
              {breadcrumb.map((b, i) => {
                const isLast = i === breadcrumb.length - 1;
                return (
                  <React.Fragment key={`${b.label}-${i}`}>
                    {b.to && !isLast ? (
                      <Link to={b.to} className="hover:underline" style={{ color: "var(--osx-brand-600)" }}>
                        {b.label}
                      </Link>
                    ) : (
                      <span style={{ color: isLast ? "var(--osx-text)" : "var(--osx-text-muted)" }}>{b.label}</span>
                    )}
                    {!isLast && <ChevronRight size={12} />}
                  </React.Fragment>
                );
              })}
            </nav>
          )}

          {tag && (
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-5"
              style={{
                background: "var(--osx-surface-2)",
                border: "1px solid var(--osx-border-strong)",
                color: "var(--osx-brand-600)",
              }}
            >
              {TagIcon && <TagIcon size={13} />}
              {tag.label}
            </motion.div>
          )}

          {headline && (
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight"
              style={{ color: "var(--osx-text)" }}
              data-testid={`${testIdPrefix}-headline`}
            >
              {headline.prefix}{" "}
              <span className="osx-gradient-text">{headline.accent}</span>
              {headline.suffix ? ` ${headline.suffix}` : ""}
            </motion.h1>
          )}

          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="mt-4 max-w-2xl text-base sm:text-lg"
              style={{ color: "var(--osx-text-muted)" }}
            >
              {subtitle}
            </motion.p>
          )}

          {lastUpdated && (
            <div
              className="mt-5 inline-flex items-center gap-2 text-[11px] font-mono px-3 py-1.5 rounded-md"
              style={{
                background: "var(--osx-surface-2)",
                border: "1px solid var(--osx-border)",
                color: "var(--osx-text-muted)",
              }}
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--osx-brand-600)" }} />
              Última actualización: {lastUpdated}
            </div>
          )}
        </div>
      </section>

      <section className="relative pb-24">
        <div className="osx-container max-w-4xl space-y-5">
          {sections.map((s, idx) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: idx * 0.08 }}
                className="relative p-7 sm:p-9 rounded-2xl osx-gradient-border"
                style={{
                  background: "var(--osx-surface)",
                  border: "1px solid var(--osx-border)",
                  boxShadow: "var(--osx-shadow-1)",
                }}
                data-testid={`${testIdPrefix}-section-${s.id}`}
              >
                <div className="flex items-start gap-4">
                  {Icon && (
                    <div
                      className="shrink-0 inline-flex h-12 w-12 items-center justify-center rounded-xl text-white"
                      style={{
                        background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))",
                        boxShadow: "0 10px 24px rgba(2,132,199,0.30)",
                      }}
                    >
                      <Icon size={20} strokeWidth={2.2} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h2 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ color: "var(--osx-text)" }}>
                        {s.title}
                      </h2>
                      {s.tag && (
                        <span
                          className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-md shrink-0"
                          style={{
                            background: "var(--osx-surface-2)",
                            color: "var(--osx-text-muted)",
                            border: "1px solid var(--osx-border)",
                          }}
                        >
                          {s.tag}
                        </span>
                      )}
                    </div>
                    {s.body && (
                      <p className="mt-3 leading-relaxed" style={{ color: "var(--osx-text-muted)" }}>
                        {s.body}
                      </p>
                    )}
                    {s.bullets && s.bullets.length > 0 && (
                      <ul className="mt-3 space-y-2">
                        {s.bullets.map((b) => (
                          <li key={b} className="flex items-start gap-2.5 text-sm" style={{ color: "var(--osx-text-muted)" }}>
                            <span
                              className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full shrink-0"
                              style={{ background: "var(--osx-brand-600)" }}
                            />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {s.callout && (
                      <div
                        className="mt-4 p-4 rounded-xl flex items-start gap-3"
                        style={{
                          background: s.callout.variant === "warning"
                            ? "color-mix(in oklab, #f59e0b 10%, var(--osx-surface-2))"
                            : "color-mix(in oklab, var(--osx-brand-600) 10%, var(--osx-surface-2))",
                          border: `1px solid ${s.callout.variant === "warning" ? "rgba(245,158,11,0.35)" : "var(--osx-border-strong)"}`,
                        }}
                      >
                        <div className="text-xs font-semibold" style={{ color: s.callout.variant === "warning" ? "#d97706" : "var(--osx-text)" }}>
                          {s.callout.label}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.article>
            );
          })}

          {(ctaTitle || ctaLinks.length > 0) && (
            <div
              className="mt-10 p-6 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
              style={{
                background:
                  "linear-gradient(135deg, color-mix(in oklab, var(--osx-brand-600) 12%, var(--osx-surface)), var(--osx-surface))",
                border: "1px solid var(--osx-border-strong)",
              }}
            >
              {(ctaTitle || ctaDescription) && (
                <div>
                  {ctaTitle && (
                    <div className="text-base font-bold" style={{ color: "var(--osx-text)" }}>
                      {ctaTitle}
                    </div>
                  )}
                  {ctaDescription && (
                    <div className="text-sm mt-1" style={{ color: "var(--osx-text-muted)" }}>
                      {ctaDescription}
                    </div>
                  )}
                </div>
              )}
              {ctaLinks.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {ctaLinks.map((l) => {
                    const style = l.primary
                      ? {
                          background: "linear-gradient(135deg, var(--osx-brand-600), var(--osx-accent-cyan))",
                          boxShadow: "0 10px 24px rgba(2,132,199,0.3)",
                          color: "#ffffff",
                        }
                      : {
                          background: "var(--osx-surface)",
                          border: "1px solid var(--osx-border-strong)",
                          color: "var(--osx-text)",
                        };
                    const cls = "inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold";
                    if (l.to) {
                      return (
                        <Link key={l.label} to={l.to} className={cls} style={style} data-testid={l.testId}>
                          {l.label}
                        </Link>
                      );
                    }
                    return (
                      <a
                        key={l.label}
                        href={l.href}
                        target="_blank"
                        rel="noreferrer noopener"
                        className={cls}
                        style={style}
                        data-testid={l.testId}
                      >
                        {l.label}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </PageLayout>
  );
};
